import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Calendar, Activity, DollarSign, Download } from 'lucide-react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const EmployeeReport: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const employeeIdFromUrl = searchParams.get('employeeId');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(
    employeeIdFromUrl ? parseInt(employeeIdFromUrl) : null
  );
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });

  const [expandedSessions, setExpandedSessions] = useState<Set<string | number>>(new Set());

  // Auto-select employee if employeeId is in URL
  useEffect(() => {
    if (employeeIdFromUrl) {
      setSelectedEmployeeId(parseInt(employeeIdFromUrl));
    }
  }, [employeeIdFromUrl]);

  // Fetch employee meter entries
  const { data: employeeMeterData, isLoading: meterDataLoading } = useQuery({
    queryKey: ['employee-meter-entries', selectedEmployeeId, dateRange],
    queryFn: () => 
      apiService.get(`/meter-entries/employee/${selectedEmployeeId}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`),
    enabled: selectedEmployeeId !== null,
  });

  // Fetch all employees for dropdown
  const { data: employeesResponse } = useQuery({
    queryKey: ['employees'],
    queryFn: () => apiService.get('/employees'),
  });

  const employees = employeesResponse?.employees || [];
  const selectedEmployeeData = employees.find((emp: any) => emp.id === selectedEmployeeId);

  const handleDownloadReport = () => {
    toast.success('Employee report downloaded successfully!');
  };

  const toggleSession = (sessionId: string | number) => {
    const newExpanded = new Set(expandedSessions);
    if (newExpanded.has(sessionId)) {
      newExpanded.delete(sessionId);
    } else {
      newExpanded.add(sessionId);
    }
    setExpandedSessions(newExpanded);
  };

  const sessions = employeeMeterData?.sessions || [];
  const totalWorkDays = new Set(sessions.map((s: any) => s.date)).size;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/employees')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="h-5 w-5 mr-2" />
                Back to Employees
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <h1 className="text-xl font-semibold text-gray-900">Employee Report</h1>
            </div>
            <button
              onClick={handleDownloadReport}
              disabled={!selectedEmployeeId}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Report
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Employee Selector and Date Range */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Employee *</label>
                <select
                  value={selectedEmployeeId || ''}
                  onChange={(e) => {
                    const empId = e.target.value ? parseInt(e.target.value) : null;
                    setSelectedEmployeeId(empId);
                    if (empId) {
                      navigate(`/employee-report?employeeId=${empId}`);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Select Employee --</option>
                  {employees.map((emp: any) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.user.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Employee Details */}
          {selectedEmployeeData && (
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 shadow">
              <h3 className="text-base font-semibold text-gray-900 mb-4">Employee Information</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs font-medium text-gray-600">Employee Name</div>
                  <div className="text-sm font-bold text-gray-900 mt-1">{selectedEmployeeData.user.name}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600">Email</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">{selectedEmployeeData.user.email}</div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600">Employment Type</div>
                  <div className="text-sm font-semibold text-gray-900 mt-1">
                    {selectedEmployeeData.type === 'FIXED' ? 'Fixed Salary' : 'Meter-based'}
                  </div>
                </div>
                <div>
                  <div className="text-xs font-medium text-gray-600">Rate</div>
                  <div className="text-sm font-bold text-green-600 mt-1">
                    ₹{selectedEmployeeData.rate}{selectedEmployeeData.type === 'METER' ? '/meter' : '/month'}
                  </div>
                </div>
                {selectedEmployeeData.user.userRole && (
                  <div>
                    <div className="text-xs font-medium text-gray-600">Role</div>
                    <div className="text-sm font-semibold text-gray-900 mt-1">{selectedEmployeeData.user.userRole.name}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading State */}
          {meterDataLoading && (
            <div className="bg-white shadow rounded-lg p-12">
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">Loading report data...</span>
              </div>
            </div>
          )}

          {/* Meter Entries Data */}
          {selectedEmployeeId && employeeMeterData && !meterDataLoading && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Meters Produced</div>
                      <div className="text-3xl font-bold text-gray-900 mt-2">
                        {employeeMeterData.totalMeters?.toFixed(2) || 0}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">meters</div>
                    </div>
                    <Activity className="h-12 w-12 text-blue-500 opacity-80" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Shifts</div>
                      <div className="text-3xl font-bold text-gray-900 mt-2">
                        {sessions.length || 0}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">{totalWorkDays} work days</div>
                    </div>
                    <Calendar className="h-12 w-12 text-green-500 opacity-80" />
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500 hover:shadow-xl transition-shadow">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">Estimated Earnings</div>
                      <div className="text-3xl font-bold text-gray-900 mt-2">
                        ₹{selectedEmployeeData?.type === 'METER' 
                          ? ((employeeMeterData.totalMeters || 0) * (selectedEmployeeData?.rate || 0)).toFixed(2)
                          : (selectedEmployeeData?.rate || 0).toFixed(2)
                        }
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {selectedEmployeeData?.type === 'METER' ? 'based on meters' : 'monthly salary'}
                      </div>
                    </div>
                    <DollarSign className="h-12 w-12 text-purple-500 opacity-80" />
                  </div>
                </div>
              </div>

              {/* Meter Entries Table */}
              <div className="bg-white shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">Work History & Meter Entries</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Showing {sessions.length || 0} shifts from {dateRange.startDate} to {dateRange.endDate}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  {sessions && sessions.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Login Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logoff Time</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Meters</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sessions.map((session: any) => (
                          <React.Fragment key={session.id}>
                            <tr className="hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => toggleSession(session.id)}>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {new Date(session.date || session.startedAt).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric' 
                                })}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {session.startedAt ? new Date(session.startedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-'}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {session.endedAt ? new Date(session.endedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : (session.status === 'active' ? <span className="text-green-600 font-medium">Active</span> : '-')}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                                  session.shiftType === 'A' 
                                    ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' 
                                    : 'bg-blue-100 text-blue-800 border border-blue-200'
                                }`}>
                                  {session.shiftType === 'A' ? '☀️ Shift A' : '🌙 Shift B'}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                                {session.meters.toFixed(2)}m
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                                {expandedSessions.has(session.id) ? 'Collapse' : 'View Machines'}
                              </td>
                            </tr>
                            {expandedSessions.has(session.id) && (
                              <tr className="bg-gray-50/50">
                                <td colSpan={6} className="px-8 py-4">
                                  <div className="border border-gray-200 rounded-lg bg-white shadow-inner overflow-hidden">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Machine</th>
                                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Quality</th>
                                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Meters</th>
                                          <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Notes</th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {session.entries.map((entry: any) => (
                                          <tr key={entry.id}>
                                            <td className="px-4 py-2 text-xs text-gray-900">
                                              {entry.machine?.number}
                                              {entry.machine?.group && <span className="ml-1 text-gray-400">({entry.machine.group.name})</span>}
                                            </td>
                                            <td className="px-4 py-2 text-xs text-gray-500">
                                              {entry.production?.quality?.name || '-'}
                                            </td>
                                            <td className="px-4 py-2 text-xs font-semibold text-green-600">
                                              {entry.meters.toFixed(2)}m
                                            </td>
                                            <td className="px-4 py-2 text-xs text-gray-400 italic">
                                              {entry.notes || '-'}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan={4} className="px-6 py-4 text-sm font-bold text-gray-900 text-right">
                            TOTAL:
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <span className="font-bold text-green-600 text-lg">
                              {employeeMeterData.totalMeters?.toFixed(2) || 0}m
                            </span>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  ) : (
                    <div className="text-center py-16">
                      <Activity className="mx-auto h-16 w-16 text-gray-300" />
                      <h3 className="mt-4 text-lg font-medium text-gray-900">No Entries Found</h3>
                      <p className="mt-2 text-sm text-gray-500">
                        No meter entries found for the selected employee and date range.
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        Try adjusting the date range or selecting a different employee.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* No Employee Selected State */}
          {!selectedEmployeeId && !meterDataLoading && (
            <div className="bg-white shadow rounded-lg p-16 text-center">
              <div className="mx-auto h-24 w-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
                <Activity className="h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-medium text-gray-900 mb-2">Select an Employee</h3>
              <p className="text-sm text-gray-500 max-w-md mx-auto">
                Choose an employee from the dropdown above to view their detailed performance report including meter entries, earnings, and work history.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeReport;
